import { atom, Getter } from 'jotai';
import { atomFamily } from 'jotai/utils';

import { purchaseOrderDeleteAtom } from '../_graphql/mutationPurchaseOrderDelete';
import { purchaseOrderUpdateAtom } from '../_graphql/mutationPurchaseOrderUpdate';
import { purchaseOrderDetailQueryAtom } from '../_graphql/queryPurchaseOrderDetail';
import { tradeRoutePaths as tradeRoutes } from '../routes';
import {
  PurchaseOrderDetailsTab,
  PurchaseOrderLineItemRecord,
} from '../types/purchaseOrder';

import { atomFromQueryString, atomFromRoutePath } from '~/shared/state';

// 🟣🟣🟣 PARAMS - Purchase Order Detail View
export const $purchaseOrderDetailsPathParams = atomFromRoutePath(
  tradeRoutes.purchaseOrderDetail,
);
export const $purchaseOrderId = atom((get) =>
  get($purchaseOrderDetailsPathParams)?.id
    ? String(get($purchaseOrderDetailsPathParams)?.id)
    : null,
);

// 🟢🟢🟢 REMOTE DATA - Purchase Order Detail
export const $remotePurchaseOrderDetail = purchaseOrderDetailQueryAtom(
  (get) => ({
    variables: { id: get($purchaseOrderId) ?? '' },
    skip: !get($purchaseOrderId),
  }),
);

// 🟢🟢🟢 REMOTE DATA - Purchase Order Detail by ID (for embedded use)
// Uses atomFamily to memoize atoms by ID - prevents infinite re-render loops
//
// Memory management: These atomFamily instances are cleaned up via useAtomFamilyCleanup
// in _PurchaseOrderDetailView.tsx with a 60-second delay after unmount. This allows quick
// back-navigation without re-fetching while preventing unbounded memory growth.
export const $purchaseOrderDetailById = atomFamily((purchaseOrderId: string) =>
  purchaseOrderDetailQueryAtom(() => ({
    variables: { id: purchaseOrderId },
    skip: !purchaseOrderId,
  })),
);

const refetchPurchaseOrderDetail = (get: Getter) => {
  const purchaseOrderId = get($purchaseOrderId);
  if (!purchaseOrderId) {
    return;
  }
  get($remotePurchaseOrderDetail).refetch?.();
};

// 🟠🟠🟠 ACTIONS - Purchase Order Update
export const $updatePurchaseOrder = purchaseOrderUpdateAtom({
  onSuccess: (get, set) => {
    set($currentDraftLineItems, null);
    refetchPurchaseOrderDetail(get);
    set($syncDraftWithRemote, true);
  },
});

// 🟠🟠🟠 ACTIONS - Purchase Order Delete
export const $deletePurchaseOrder = purchaseOrderDeleteAtom({
  onSuccess: (get) => {
    refetchPurchaseOrderDetail(get);
  },
});

// 🔘🔘🔘 PLAIN ATOMS - UI State
export const $tab = atomFromQueryString.string<PurchaseOrderDetailsTab>('tab');
export const $isEditMode = atom<boolean>(false);
export const $hasUnsavedChanges = atom<boolean>(false);

export const $isPurchaseOrderLoading = atom((get) => {
  const data = get($remotePurchaseOrderDetail);
  return data?.isLoading ?? false;
});

export const $purchaseOrderError = atom((get) => {
  const data = get($remotePurchaseOrderDetail);
  return data?.error ?? null;
});

// 🟢🟢🟢 COMPUTED DATA - Line Items Data
export const $lineItemsData = atom((get) => {
  const data = get($remotePurchaseOrderDetail);
  return data?.data?.lineItems ?? [];
});

export const $isLineItemsLoading = atom((get) => {
  const data = get($remotePurchaseOrderDetail);
  return data?.isLoading ?? false;
});

// 🟡🟡🟡 DRAFT STATE - Material Line Items Draft Changes
export const $draftLineItems = atom<Map<string, PurchaseOrderLineItemRecord[]>>(
  new Map(),
);

export const $currentDraftLineItems = atom(
  (get) => {
    const purchaseOrderId = get($purchaseOrderId);
    const draftMap = get($draftLineItems);
    return purchaseOrderId ? (draftMap.get(purchaseOrderId) ?? null) : null;
  },
  (get, set, lineItems: PurchaseOrderLineItemRecord[] | null) => {
    const purchaseOrderId = get($purchaseOrderId);
    if (!purchaseOrderId) {
      return;
    }

    set($draftLineItems, (prev) => {
      const newMap = new Map(prev);
      if (lineItems === null) {
        newMap.delete(purchaseOrderId);
      } else {
        newMap.set(purchaseOrderId, lineItems);
      }
      return newMap;
    });
  },
);

export const $effectiveLineItems = atom((get) => {
  const purchaseOrder = get($remotePurchaseOrderDetail);
  const draftLineItems = get($currentDraftLineItems);
  const remoteLineItems = purchaseOrder?.data?.lineItems ?? [];

  if (draftLineItems !== null) {
    return draftLineItems;
  }

  return remoteLineItems;
});

export const $lastSavedLineItemsTimestamp = atom<string | null>(null);

export const $syncDraftWithRemote = atom(
  (get) => {
    const purchaseOrder = get($remotePurchaseOrderDetail);
    const lastTimestamp = get($lastSavedLineItemsTimestamp);

    if (purchaseOrder?.data?.timestamps?.updatedAt) {
      return (
        purchaseOrder.data.timestamps.updatedAt.toString() !== lastTimestamp
      );
    }
    return false;
  },
  (get, set, shouldClearDraft = false) => {
    const purchaseOrder = get($remotePurchaseOrderDetail);
    const currentDraft = get($currentDraftLineItems);

    if (purchaseOrder?.data?.timestamps?.updatedAt) {
      const currentTimestamp =
        purchaseOrder.data.timestamps.updatedAt.toString();

      set($lastSavedLineItemsTimestamp, currentTimestamp);

      if (shouldClearDraft && currentDraft !== null) {
        set($currentDraftLineItems, null);
      }
    }
  },
);

export const $hasDraftChanges = atom((get) => {
  const purchaseOrder = get($remotePurchaseOrderDetail);
  const draftLineItems = get($currentDraftLineItems);

  if (!purchaseOrder?.data || draftLineItems === null) {
    return false;
  }

  const originalLineItems = purchaseOrder.data.lineItems ?? [];

  if (originalLineItems.length !== draftLineItems.length) {
    return true;
  }

  return draftLineItems.some((draftItem, index) => {
    const originalItem = originalLineItems[index];
    if (!originalItem) return true;

    return (
      draftItem.itemId !== originalItem.itemId ||
      draftItem.quantity !== originalItem.quantity ||
      draftItem.unit !== originalItem.unit ||
      draftItem.unitPrice?.amount !== originalItem.unitPrice?.amount ||
      draftItem.unitPrice?.currencyCode !== originalItem.unitPrice?.currencyCode
    );
  });
});

export const $clearCurrentDraftLineItems = atom(null, (get, set) => {
  set($currentDraftLineItems, null);
});

export const $cleanupDraftState = atom(null, (get, set) => {
  const currentPurchaseOrderId = get($purchaseOrderId);
  const draftMap = get($draftLineItems);

  if (!currentPurchaseOrderId) {
    set($draftLineItems, new Map());
    return;
  }

  const newMap = new Map();
  const currentDraft = draftMap.get(currentPurchaseOrderId);
  if (currentDraft) {
    newMap.set(currentPurchaseOrderId, currentDraft);
  }
  set($draftLineItems, newMap);
});

// 🟡🟡🟡 DRAFT STATE BY ID - For embedded use where purchaseOrderId is passed as prop
export const $currentDraftLineItemsById = atomFamily(
  (purchaseOrderId: string) =>
    atom(
      (get) => {
        const draftMap = get($draftLineItems);
        return purchaseOrderId ? (draftMap.get(purchaseOrderId) ?? null) : null;
      },
      (get, set, lineItems: PurchaseOrderLineItemRecord[] | null) => {
        if (!purchaseOrderId) {
          return;
        }

        set($draftLineItems, (prev) => {
          const newMap = new Map(prev);
          if (lineItems === null) {
            newMap.delete(purchaseOrderId);
          } else {
            newMap.set(purchaseOrderId, lineItems);
          }
          return newMap;
        });
      },
    ),
);

export const $effectiveLineItemsById = atomFamily((purchaseOrderId: string) =>
  atom((get) => {
    const purchaseOrder = get($purchaseOrderDetailById(purchaseOrderId));
    const draftLineItems = get($currentDraftLineItemsById(purchaseOrderId));
    const remoteLineItems = purchaseOrder?.data?.lineItems ?? [];

    if (draftLineItems !== null) {
      return draftLineItems;
    }

    return remoteLineItems;
  }),
);

export const $hasDraftChangesById = atomFamily((purchaseOrderId: string) =>
  atom((get) => {
    const purchaseOrder = get($purchaseOrderDetailById(purchaseOrderId));
    const draftLineItems = get($currentDraftLineItemsById(purchaseOrderId));

    if (!purchaseOrder?.data || draftLineItems === null) {
      return false;
    }

    const originalLineItems = purchaseOrder.data.lineItems ?? [];

    if (originalLineItems.length !== draftLineItems.length) {
      return true;
    }

    return draftLineItems.some((draftItem, index) => {
      const originalItem = originalLineItems[index];
      if (!originalItem) return true;

      return (
        draftItem.itemId !== originalItem.itemId ||
        draftItem.quantity !== originalItem.quantity ||
        draftItem.unit !== originalItem.unit ||
        draftItem.unitPrice?.amount !== originalItem.unitPrice?.amount ||
        draftItem.unitPrice?.currencyCode !==
          originalItem.unitPrice?.currencyCode
      );
    });
  }),
);
